import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Affix, Box, Portal, ScrollArea, Stack, Space } from "@mantine/core";
import { ControlActionIcon } from "components/LeafletMap/MapControls/ControlActionIcon";
import {
  getTangramLayerMeta,
  isTangramLayer,
} from "components/LeafletMap/TangramLayersMeta";
import { Tooltip } from "components/UI/Tooltip";
import { useTranslation } from "react-i18next";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { LabelCountSlider } from "./parts/LabelCountSlider";
import { MapColorPicker } from "./parts/MapColorPicker";
import { MapOverlayPicker } from "./parts/MapOverlayPicker";
import { MapTypePicker } from "./parts/MapTypePicker";
import classes from "./MapLayersPickerOverlay.module.css";
import scrollAreaClasses from "components/UI/ScrollArea.module.css";

export const MapLayersPickerOverlay = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { state } = useOptions();
  const { t } = useTranslation();
  const [isLayerTypeTangram, setIsLayerTypeTangram] = useState(
    isTangramLayer(state.mapType)
  );
  const ignoreClicks = useRef(false);

  const setIgnoreClicks = useCallback((value: boolean) => {
    ignoreClicks.current = value;
  }, []);

  useEffect(() => {
    setIsLayerTypeTangram(isTangramLayer(state.mapType));
  }, [state.mapType]);

  const currentTangramLayerMeta = getTangramLayerMeta(state.mapType);

  const handleAffixClick = () => {
    if (ignoreClicks.current) return;
    setIsOpen(false);
  };

  return (
    <div>
      <Tooltip label={t("map_controls.maps_button_tooltip")} position="left">
        <ControlActionIcon
          onClick={() => setIsOpen((prev) => !prev)}
          aria-label={t("map_controls.maps_button_tooltip")}
          aria-expanded={isOpen}
        >
          <FontAwesomeIcon icon={faLayerGroup} />
        </ControlActionIcon>
      </Tooltip>

      {isOpen && (
        <Portal target={document.body}>
          {/* Position and z-index have to be set via props - Affix defaults to bottom right
              and its own CSS would otherwise override them depending on stylesheet order */}
          <Affix
            onClick={handleAffixClick}
            position={{ top: 0, left: 0, right: 0, bottom: 0 }}
            zIndex={1000}
            className={classes.overlay}
          >
            <ScrollArea
              type="scroll"
              scrollHideDelay={20000}
              styles={{ root: { height: "100%" } }}
              classNames={{ thumb: scrollAreaClasses.thumb }}
            >
              <Box
                style={{ display: "flex", justifyContent: "flex-end" }}
                p="xs"
                mr={40}
              >
                <Stack gap={0}>
                  <Stack>
                    <MapTypePicker />
                    <Space h="xs" />
                  </Stack>
                  {isLayerTypeTangram && (
                    <Stack>
                      <MapOverlayPicker />
                      <Space h="xs" />
                    </Stack>
                  )}
                  {isLayerTypeTangram && currentTangramLayerMeta?.colors && (
                    <Stack>
                      <Suspense fallback={null}>
                        <MapColorPicker />
                      </Suspense>
                      <Space h="xs" />
                    </Stack>
                  )}
                  {currentTangramLayerMeta?.hasLabels && (
                    <LabelCountSlider setIgnoreClicks={setIgnoreClicks} />
                  )}
                </Stack>
              </Box>
            </ScrollArea>
          </Affix>
        </Portal>
      )}
    </div>
  );
};
